import { useState, useEffect } from 'react';
import {
  MagnifyingGlass,
  SortAscending,
  SortDescending,
  CaretLeft,
  CaretRight,
  CaretDoubleLeft,
  CaretDoubleRight,
  Plus,
  Pencil,
} from '@phosphor-icons/react';
import { WorkshopModal } from '../../components/WorkshopModal';
// import { WorkshopModal } from "./WorkshopModal";

export function Workshops() {
  const [workshops, setWorkshops] = useState([]);
  const [filteredWorkshops, setFilteredWorkshops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalLoading, setModalLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [sortField, setSortField] = useState('nome');
  const [sortDirection, setSortDirection] = useState('asc');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingWorkshop, setEditingWorkshop] = useState(null);

  // Carregar oficinas da API
  useEffect(() => {
    fetchWorkshops();
  }, [itemsPerPage]);

  const fetchWorkshops = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        'https://radar.tlpservicos.com.br/api/manutencao/oficina?skip=0&limit=100'
      );

      if (response.ok) {
        const data = await response.json();
        setWorkshops(data);
        setFilteredWorkshops(data);
        setTotalPages(Math.ceil(data.length / itemsPerPage));
      } else {
        showToast('Erro ao carregar oficinas', 'error');
      }
    } catch (error) {
      console.error('Erro ao buscar oficinas:', error);
      showToast('Erro ao carregar a lista de oficinas', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Formatar data e hora
  const formatDateTime = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch (error) {
      return 'Data inválida';
    }
  };

  // Filtrar, ordenar e paginar
  useEffect(() => {
    let result = [...workshops];

    // Filtrar
    if (searchTerm) {
      result = result.filter(
        (workshop) =>
          workshop.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
          workshop.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          workshop.telefone.toLowerCase().includes(searchTerm.toLowerCase()) ||
          workshop.endereco.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Ordenar
    result.sort((a, b) => {
      const fieldA = a[sortField];
      const fieldB = b[sortField];

      if (typeof fieldA === 'string' && typeof fieldB === 'string') {
        return sortDirection === 'asc'
          ? fieldA.localeCompare(fieldB)
          : fieldB.localeCompare(fieldA);
      }

      return sortDirection === 'asc' ? fieldA - fieldB : fieldB - fieldA;
    });

    setFilteredWorkshops(result);
    setTotalPages(Math.ceil(result.length / itemsPerPage));
    setCurrentPage(1);
  }, [searchTerm, workshops, sortField, sortDirection, itemsPerPage]);

  // Função para ordenar tabela
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Ícone de ordenação
  const renderSortIcon = (field) => {
    if (sortField !== field) return null;

    return sortDirection === 'asc' ? (
      <SortAscending size={16} className="ml-1" />
    ) : (
      <SortDescending size={16} className="ml-1" />
    );
  };

  // Abrir modal para adicionar
  const handleOpenAddModal = () => {
    setShowAddModal(true);
  };

  // Abrir modal para editar
  const handleOpenEditModal = (workshop) => {
    setEditingWorkshop(workshop);
  };

  // Fechar modais
  const handleCloseModals = () => {
    setShowAddModal(false);
    setEditingWorkshop(null);
  };

  // Adicionar nova oficina
  const handleAddWorkshop = async (formData) => {
    try {
      setModalLoading(true);
      const response = await fetch(
        'https://radar.tlpservicos.com.br/api/manutencao/oficina',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData),
        }
      );

      if (response.ok) {
        const newWorkshop = await response.json();
        setWorkshops((prev) => [...prev, newWorkshop]);
        setShowAddModal(false);
        showToast('Oficina cadastrada com sucesso!');
      } else {
        showToast('Erro ao cadastrar oficina', 'error');
      }
    } catch (error) {
      console.error('Erro ao cadastrar oficina:', error);
      showToast('Erro ao cadastrar oficina', 'error');
    } finally {
      setModalLoading(false);
    }
  };

  // Editar oficina
  const handleEditWorkshop = async (formData) => {
    try {
      setModalLoading(true);
      const response = await fetch(
        `https://radar.tlpservicos.com.br/api/manutencao/oficina/${editingWorkshop.id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData),
        }
      );

      if (response.ok) {
        const updatedWorkshop = await response.json();
        setWorkshops((prev) =>
          prev.map((workshop) =>
            workshop.id === editingWorkshop.id ? updatedWorkshop : workshop
          )
        );
        setEditingWorkshop(null);
        showToast('Oficina atualizada com sucesso!');
      } else {
        showToast('Erro ao atualizar oficina', 'error');
      }
    } catch (error) {
      console.error('Erro ao atualizar oficina:', error);
      showToast('Erro ao atualizar oficina', 'error');
    } finally {
      setModalLoading(false);
    }
  };

  // Paginação
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredWorkshops.slice(indexOfFirstItem, indexOfLastItem);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const goToFirstPage = () => setCurrentPage(1);
  const goToLastPage = () => setCurrentPage(totalPages);
  const nextPage = () => currentPage < totalPages && setCurrentPage(currentPage + 1);
  const prevPage = () => currentPage > 1 && setCurrentPage(currentPage - 1);

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value));
  };

  return (
    <div className="p-6 rounded-md shadow bg-gray-300 mx-2">
      {/* Cabeçalho */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">Cadastro de Oficinas</h1>
        <button
          onClick={handleOpenAddModal}
          className="flex items-center px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
        >
          <Plus size={20} className="mr-2" />
          Nova Oficina
        </button>
      </div>

      {/* Filtros e controles */}
      <div className="mb-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="relative w-full md:w-64">
          <input
            type="text"
            placeholder="Buscar por nome, email, telefone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full border border-gray-300 rounded-md pl-10 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900"
          />
          <MagnifyingGlass
            size={20}
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500"
          />
        </div>

        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-200">Itens por página:</span>
          <select
            value={itemsPerPage}
            onChange={handleItemsPerPageChange}
            className="border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900"
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </select>
        </div>
      </div>

      {/* Tabela */}
      <div className="overflow-x-auto bg-gray-300 rounded-md">
        <table className="min-w-full divide-y divide-gray-400">
          <thead className="bg-gray-350">
            <tr>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-200 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('nome')}
              >
                <div className="flex items-center">Nome {renderSortIcon('nome')}</div>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-200 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('email')}
              >
                <div className="flex items-center">Email {renderSortIcon('email')}</div>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-200 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('telefone')}
              >
                <div className="flex items-center">
                  Telefone {renderSortIcon('telefone')}
                </div>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-200 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('endereco')}
              >
                <div className="flex items-center">
                  Endereço {renderSortIcon('endereco')}
                </div>
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-200 uppercase tracking-wider cursor-pointer"
                onClick={() => handleSort('atualizado_em')}
              >
                <div className="flex items-center">
                  Última Atualização {renderSortIcon('atualizado_em')}
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-200 uppercase tracking-wider">
                Ações
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={6} className="px-6 py-4 text-center text-gray-900">
                  <div className="flex justify-center items-center">
                    <svg
                      className="animate-spin h-5 w-5 text-blue-600 mr-3"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Carregando oficinas...
                  </div>
                </td>
              </tr>
            ) : currentItems.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                  Nenhuma oficina encontrada.
                </td>
              </tr>
            ) : (
              currentItems.map((workshop) => (
                <tr key={workshop.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-gray-900">
                    <div className="font-medium">{workshop.nome}</div>
                    <div className="text-xs text-gray-500">ID: {workshop.id}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-900">
                    {workshop.email}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-900">
                    {workshop.telefone}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-900">
                    <div className="max-w-xs truncate" title={workshop.endereco}>
                      {workshop.endereco}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-900">
                    {formatDateTime(workshop.atualizado_em)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      className="p-1 transition-colors"
                      title="Editar"
                      onClick={() => handleOpenEditModal(workshop)}
                    >
                      <Pencil
                        size={20}
                        className="text-blue-500 hover:text-blue-700"
                        weight="bold"
                      />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Paginação */}
      {totalPages > 0 && (
        <div className="py-3 flex items-center justify-between border-t border-gray-400 mt-4">
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-200">
                Mostrando <span className="font-medium">{indexOfFirstItem + 1}</span> a{' '}
                <span className="font-medium">
                  {Math.min(indexOfLastItem, filteredWorkshops.length)}
                </span>{' '}
                de <span className="font-medium">{filteredWorkshops.length}</span>{' '}
                resultados
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                <button
                  onClick={goToFirstPage}
                  disabled={currentPage === 1}
                  className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${
                    currentPage === 1
                      ? 'text-gray-300 cursor-not-allowed'
                      : 'text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  <CaretDoubleLeft size={18} />
                </button>
                <button
                  onClick={prevPage}
                  disabled={currentPage === 1}
                  className={`relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium ${
                    currentPage === 1
                      ? 'text-gray-300 cursor-not-allowed'
                      : 'text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  <CaretLeft size={18} />
                </button>

                {Array.from({ length: Math.min(5, totalPages) }).map((_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }

                  return (
                    <button
                      key={i}
                      onClick={() => paginate(pageNum)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        currentPage === pageNum
                          ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}

                <button
                  onClick={nextPage}
                  disabled={currentPage === totalPages}
                  className={`relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium ${
                    currentPage === totalPages
                      ? 'text-gray-300 cursor-not-allowed'
                      : 'text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  <CaretRight size={18} />
                </button>
                <button
                  onClick={goToLastPage}
                  disabled={currentPage === totalPages}
                  className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${
                    currentPage === totalPages
                      ? 'text-gray-300 cursor-not-allowed'
                      : 'text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  <CaretDoubleRight size={18} />
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      <WorkshopModal
        isOpen={showAddModal}
        onClose={handleCloseModals}
        onSubmit={handleAddWorkshop}
        loading={modalLoading}
      />

      <WorkshopModal
        isOpen={!!editingWorkshop}
        onClose={handleCloseModals}
        onSubmit={handleEditWorkshop}
        workshop={editingWorkshop}
        loading={modalLoading}
      />
    </div>
  );
}
